import React, { useState, useEffect } from 'react';
import { X, RefreshCw, Edit, Save, XCircle, Trash2, Loader2 } from 'lucide-react';
import api, { deleteDeliveryNote } from '../../services/api';
import reprocessingService from '../../services/reprocessing.service';

const DetailedDeliveryNoteModal = ({ isOpen, onClose, data, setData, onRefresh }) => {
  // View modes: 'data', 'pdf', 'split'
  const [viewMode, setViewMode] = useState('split');
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfError, setPdfError] = useState(false);

  // Reprocessing state
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [reprocessingTaskId, setReprocessingTaskId] = useState(null);
  const [reprocessingStatus, setReprocessingStatus] = useState(null);
  const [reprocessingProgress, setReprocessingProgress] = useState(0);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Delete state
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Function to get a fresh SAS URL for a blob path
  const getPdfUrl = async (blobPath, accountName, containerName) => {
    if (!blobPath) return null;

    try {
      // If it's already a full URL (old format), return it directly
      if (blobPath.startsWith('http')) {
        return blobPath;
      }

      console.log('Getting fresh SAS token for:', { blobPath, accountName, containerName });

      // Get a fresh SAS token from the API
      // The authentication token will be automatically included by the axios interceptor
      const url = `/api/files/pdf-proxy?blobPath=${encodeURIComponent(blobPath)}`;
      const queryParams = {};

      if (accountName) {
        queryParams.accountName = accountName;
      }

      if (containerName) {
        queryParams.containerName = containerName;
      }

      // Build the full URL with query parameters
      const fullUrl = Object.keys(queryParams).length > 0
        ? `${url}&${new URLSearchParams(queryParams).toString()}`
        : url;

      console.log('Making request to:', fullUrl);

      const response = await api.get(fullUrl);

      console.log('PDF proxy response:', response);

      if (response.data && response.data.success && response.data.sasUrl) {
        console.log('Using SAS URL from response:', response.data.sasUrl);
        return response.data.sasUrl;
      } else {
        console.error('Invalid response format:', response.data);
        return null;
      }
    } catch (error) {
      console.error('Error getting PDF URL:', error);
      return null;
    }
  };

  // Function to find the PDF blob path from the data
  const findPdfPath = (data) => {
    if (!data) return null;

    // Try blob path fields first (new format)
    if (data.blobPath) return data.blobPath;
    if (data.originalBlobPath) return data.originalBlobPath;

    // Try direct URL fields (old format)
    // If it's a URL with a SAS token, extract just the blob path
    const extractBlobPath = (url) => {
      if (!url) return null;
      if (url.includes('blob.core.windows.net')) {
        // Extract the path part from the URL
        try {
          const urlObj = new URL(url);
          const pathMatch = urlObj.pathname.match(/\/([^\/]+)\/(.+)/);
          if (pathMatch && pathMatch.length >= 3) {
            return pathMatch[2]; // Just the blob path
          }
        } catch (e) {
          console.error('Error parsing URL:', e);
        }
      }
      return url; // Return the original URL if we can't extract a blob path
    };

    // Try direct fields first
    if (data.pagePdfUrl) return extractBlobPath(data.pagePdfUrl);
    if (data.pageUrl) return extractBlobPath(data.pageUrl);
    if (data.pdfUrl) return extractBlobPath(data.pdfUrl);
    if (data.pdfPath) return extractBlobPath(data.pdfPath);

    // Try nested in originalData
    if (data.originalData) {
      if (data.originalData.blobPath) return data.originalData.blobPath;
      if (data.originalData.originalBlobPath) return data.originalData.originalBlobPath;
      if (data.originalData.pagePdfUrl) return extractBlobPath(data.originalData.pagePdfUrl);
      if (data.originalData.pageUrl) return extractBlobPath(data.originalData.pageUrl);
      if (data.originalData.pdfUrl) return extractBlobPath(data.originalData.pdfUrl);
      if (data.originalData.pdfPath) return extractBlobPath(data.originalData.pdfPath);
    }

    return null;
  };

  // Reference to store the interval ID
  const statusIntervalRef = React.useRef(null);

  // Function to handle saving edited data
  const handleSaveEdits = async () => {
    if (!editedData) return;

    try {
      setIsSaving(true);

      // Determine the document ID to use (prefer document number over MongoDB ID)
      let documentId = null;
      if (data.standard?.documentInfo?.documentNumber) {
        documentId = data.standard.documentInfo.documentNumber;
      } else if (data._id) {
        documentId = data._id;
      } else {
        console.error('No document ID or number found');
        return;
      }

      // Determine the collection name
      let collectionName = null;
      if (data.collection) {
        collectionName = data.collection;
      } else if (data.projectId) {
        collectionName = data.projectId;
      } else if (data.project) {
        collectionName = data.project;
      } else if (data.id) {
        collectionName = data.id;
      }

      // Construct the API endpoint
      let endpoint = `/api/delivery-notes/document/${documentId}`;
      if (collectionName) {
        endpoint += `?collection=${encodeURIComponent(collectionName)}`;
      }

      console.log(`Saving document with ID: ${documentId}, Collection: ${collectionName || 'auto-detect'}`);

      // Make the API call to update the document
      const response = await api.put(endpoint, editedData);

      if (response.data && response.data.success) {
        console.log('Document updated successfully:', response.data);

        // Update the data in the parent component
        if (typeof setData === 'function') {
          setData(response.data.data);
        }

        // Exit edit mode
        setIsEditMode(false);
      } else {
        console.error('Failed to update document:', response.data);
      }
    } catch (error) {
      console.error('Error updating document:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Function to handle field changes in edit mode
  const handleFieldChange = (path, value) => {
    if (!editedData) return;

    // Create a deep copy of the edited data
    const updatedData = JSON.parse(JSON.stringify(editedData));

    // Split the path into parts
    const pathParts = path.split('.');

    // Navigate to the nested property
    let current = updatedData;
    for (let i = 0; i < pathParts.length - 1; i++) {
      const part = pathParts[i];

      // Create the object if it doesn't exist
      if (!current[part]) {
        current[part] = {};
      }

      current = current[part];
    }

    // Set the value
    current[pathParts[pathParts.length - 1]] = value;

    // Update the state
    setEditedData(updatedData);
  };

  // Function to handle reprocessing of the delivery note
  const handleReprocess = async () => {
    try {
      // Clear any existing interval
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current);
        statusIntervalRef.current = null;
      }

      // Get the necessary data for reprocessing
      const documentId = data._id;

      // Try to determine the collection name, but it's now optional
      // The backend will try to find the correct collection if not provided
      let collectionName = null;

      // Check various possible locations for the collection name
      if (data.collection) {
        collectionName = data.collection;
      } else if (data.projectId) {
        collectionName = data.projectId;
      } else if (data.project) {
        collectionName = data.project;
      } else if (data.id) {
        // The id field often contains the project/collection name
        collectionName = data.id;
      }

      // Log what we found (or didn't find)
      if (collectionName) {
        console.log('Found collection name for reprocessing:', collectionName);
      } else {
        console.log('No collection name found in data, backend will determine the correct collection');
      }

      const blobPath = findPdfPath(data);
      const accountName = data.accountName || null;
      const containerName = data.containerName || null;

      if (!documentId || !blobPath) {
        console.error('Missing required data for reprocessing');
        return;
      }

      // Set reprocessing state
      setIsReprocessing(true);
      setReprocessingStatus('starting');
      setReprocessingProgress(0);

      // Call the reprocessing service
      const response = await reprocessingService.reprocessDeliveryNote({
        documentId,
        collectionName,
        blobPath,
        accountName,
        containerName
      });

      if (response.success && response.task_id) {
        setReprocessingTaskId(response.task_id);
        setReprocessingStatus('processing');

        // Start polling for status
        statusIntervalRef.current = setInterval(async () => {
          try {
            const statusResponse = await reprocessingService.checkReprocessingStatus(response.task_id);

            if (statusResponse.success) {
              setReprocessingStatus(statusResponse.status);
              setReprocessingProgress(statusResponse.progress || 0);

              // If completed, clear interval and refresh data
              if (statusResponse.status === 'completed') {
                if (statusIntervalRef.current) {
                  clearInterval(statusIntervalRef.current);
                  statusIntervalRef.current = null;
                }
                setIsReprocessing(false);

                // Refresh the data by triggering a reload
                // This would typically be handled by the parent component
                // For now, we'll just close and reopen the modal
                onClose();
                // You might want to add a callback to the parent to refresh the data
              }
            }
          } catch (error) {
            console.error('Error checking reprocessing status:', error);
          }
        }, 2000); // Check every 2 seconds
      } else {
        setIsReprocessing(false);
        setReprocessingStatus('failed');
        console.error('Failed to start reprocessing:', response);
      }
    } catch (error) {
      console.error('Error reprocessing delivery note:', error);
      setIsReprocessing(false);
      setReprocessingStatus('failed');
    }
  };

  // Function to handle deletion
  const handleDelete = async () => {
    if (!data) return;

    try {
      setIsDeleting(true);

      // Determine the document ID to use (prefer document number over MongoDB ID)
      let documentId = null;
      if (data.standard?.documentInfo?.documentNumber) {
        documentId = data.standard.documentInfo.documentNumber;
      } else if (data._id) {
        documentId = data._id;
      } else {
        console.error('No document ID or number found');
        return;
      }

      // Determine the collection name
      let collectionName = null;
      if (data.collection) {
        collectionName = data.collection;
      } else if (data.projectId) {
        collectionName = data.projectId;
      } else if (data.project) {
        collectionName = data.project;
      } else if (data.id) {
        collectionName = data.id;
      }

      console.log(`Deleting document with ID: ${documentId}, Collection: ${collectionName || 'auto-detect'}`);

      // Call the delete API
      const response = await deleteDeliveryNote(documentId, collectionName);

      if (response && response.success) {
        console.log('Document deleted successfully:', response);

        // Close the modal
        onClose();

        // Refresh the data if callback is provided
        if (typeof onRefresh === 'function') {
          onRefresh();
        }
      } else {
        console.error('Failed to delete document:', response);
        alert('Fehler beim Löschen des Lieferscheins. Bitte versuchen Sie es erneut.');
      }
    } catch (error) {
      console.error('Error during deletion:', error);
      alert('Fehler beim Löschen des Lieferscheins. Bitte versuchen Sie es erneut.');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  // Clean up interval when component unmounts
  useEffect(() => {
    return () => {
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current);
        statusIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // Reset the state when the modal is opened/closed
    if (!isOpen) {
      setViewMode('split');
      setPdfUrl(null);
      setPdfError(false);
      setIsReprocessing(false);
      setReprocessingTaskId(null);
      setReprocessingStatus(null);
      setReprocessingProgress(0);
      setIsEditMode(false);
      setEditedData(null);
      setShowDeleteConfirmation(false);
      setIsDeleting(false);
    } else if (data) {
      // Initialize editedData with a deep copy of the data
      setEditedData(JSON.parse(JSON.stringify(data)));
      // Debug log to see all available data
      console.log("Delivery Note Detail Data:", data);

      // Check for PDF blob path in various possible fields
      const blobPath = findPdfPath(data);

      // Get account name and container name from the data
      const accountName = data.accountName || null;
      const containerName = data.containerName || null;

      console.log("Found PDF blob path:", blobPath);
      console.log("Account Name:", accountName);
      console.log("Container Name:", containerName);

      if (blobPath) {
        // Set loading state
        setLoading(true);

        // Get a fresh SAS URL for the blob path
        getPdfUrl(blobPath, accountName, containerName)
          .then(freshUrl => {
            if (freshUrl) {
              setPdfUrl(freshUrl);
              console.log("Using fresh SAS URL:", freshUrl);
            } else {
              console.error("Failed to get fresh SAS URL");
              setPdfError(true);
            }
          })
          .catch(error => {
            console.error("Error getting fresh SAS URL:", error);
            setPdfError(true);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }
  }, [isOpen, data]);

  if (!isOpen || !data) return null;

  // Use editedData in edit mode, otherwise use the original data
  const displayData = isEditMode && editedData ? editedData : data;
  const standardData = displayData.standard || {};
  const { documentInfo, productionPlant, constructionSite, material } = standardData;

  // Helper function to format ISO datetime to German format DD.MM.YYYY HH:mm
  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    try {
      const date = new Date(isoString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');

      return `${day}.${month}.${year} ${hours}:${minutes}`;
    } catch (e) {
      return '-';
    }
  };

  // Helper function to format weight with unit
  const formatWeight = (quantity) => {
    if (!quantity?.value) return '-';
    return `${quantity.value.toLocaleString('de-DE')} ${quantity.unit || ''}`.trim();
  };

  // Define modal size with responsive height
  const modalSizeClass = "bg-white rounded-lg w-full max-w-[1000px] max-h-[90vh] min-h-[500px] flex flex-col overflow-hidden";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className={modalSizeClass}>
        <div className="flex flex-col border-b">
          <div className="flex justify-between items-center p-4">
            <h2 className="text-xl font-semibold">
              Lieferschein - {documentInfo?.documentNumber}
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  // If in edit mode, ask for confirmation before closing
                  if (isEditMode) {
                    if (window.confirm('Möchten Sie den Bearbeitungsmodus verlassen? Nicht gespeicherte Änderungen gehen verloren.')) {
                      onClose();
                    }
                  } else {
                    onClose();
                  }
                }}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-t justify-between">
            <div className="flex">
              {/* <button
                onClick={() => {
                  // If in edit mode, ask for confirmation before switching
                  if (isEditMode) {
                    if (window.confirm('Möchten Sie den Bearbeitungsmodus verlassen? Nicht gespeicherte Änderungen gehen verloren.')) {
                      setIsEditMode(false);
                      setEditedData(JSON.parse(JSON.stringify(data)));
                      setViewMode('data');
                    }
                  } else {
                    setViewMode('data');
                  }
                }}
                className={`px-6 py-3 font-medium text-sm ${viewMode === 'data'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'}`}
              >
                Daten
              </button>
              <button
                onClick={() => {
                  // If in edit mode, ask for confirmation before switching
                  if (isEditMode) {
                    if (window.confirm('Möchten Sie den Bearbeitungsmodus verlassen? Nicht gespeicherte Änderungen gehen verloren.')) {
                      setIsEditMode(false);
                      setEditedData(JSON.parse(JSON.stringify(data)));
                      if (viewMode !== 'pdf') {
                        setLoading(true);
                      }
                      setViewMode('pdf');
                    }
                  } else {
                    if (viewMode !== 'pdf') {
                      setLoading(true);
                    }
                    setViewMode('pdf');
                  }
                }}
                className={`px-6 py-3 font-medium text-sm ${viewMode === 'pdf'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'}`}
              >
                Lieferschein
              </button> */}
              <button
                onClick={() => {
                  // If in edit mode, ask for confirmation before switching
                  if (isEditMode) {
                    if (window.confirm('Möchten Sie den Bearbeitungsmodus verlassen? Nicht gespeicherte Änderungen gehen verloren.')) {
                      setIsEditMode(false);
                      setEditedData(JSON.parse(JSON.stringify(data)));
                      if (viewMode !== 'split') {
                        setLoading(true);
                      }
                      setViewMode('split');
                    }
                  } else {
                    if (viewMode !== 'split') {
                      setLoading(true);
                    }
                    setViewMode('split');
                  }
                }}
                className={`px-6 py-3 font-medium text-sm ${viewMode === 'split'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'}`}
              >
                Geteilte Ansicht
              </button>
            </div>
            <div className="flex items-center gap-2 mr-4">
              {/* Edit button for split view */}
              {viewMode === 'split' && !isEditMode && (
                <button
                  onClick={() => setIsEditMode(true)}
                  className="flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium bg-blue-500 text-white hover:bg-blue-600"
                  title="Daten bearbeiten"
                >
                  <Edit className="h-3 w-3" />
                  Bearbeiten
                </button>
              )}

              {/* Save and Cancel buttons for edit mode */}
              {viewMode === 'split' && isEditMode && (
                <>
                  <button
                    onClick={handleSaveEdits}
                    disabled={isSaving}
                    className={`flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium ${
                      isSaving
                        ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                        : 'bg-green-500 text-white hover:bg-green-600'
                    }`}
                    title="Änderungen speichern"
                  >
                    <Save className="h-3 w-3" />
                    {isSaving ? 'Wird gespeichert...' : 'Speichern'}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditMode(false);
                      setEditedData(JSON.parse(JSON.stringify(data)));
                    }}
                    className="flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium bg-red-500 text-white hover:bg-red-600"
                    title="Bearbeitung abbrechen"
                  >
                    <XCircle className="h-3 w-3" />
                    Abbrechen
                  </button>
                </>
              )}

              {/* Reprocess button */}
              <button
                onClick={handleReprocess}
                disabled={isReprocessing}
                className={`flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium ${
                  isReprocessing
                    ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
                title="Seite neu verarbeiten"
              >
                <RefreshCw className={`h-3 w-3 ${isReprocessing ? 'animate-spin' : ''}`} />
                {isReprocessing ? 'Wird verarbeitet...' : 'Neu verarbeiten'}
              </button>

              {/* Delete button */}
              <button
                onClick={() => setShowDeleteConfirmation(true)}
                disabled={isDeleting || isReprocessing || isEditMode}
                className={`flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium ${
                  isDeleting || isReprocessing || isEditMode
                    ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                    : 'bg-red-500 text-white hover:bg-red-600'
                }`}
                title="Lieferschein löschen"
              >
                <Trash2 className="h-3 w-3" />
                Löschen
              </button>
            </div>
          </div>
        </div>

        {viewMode === 'pdf' ? (
          <div className="flex-1 bg-gray-100 relative">
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              </div>
            )}

            {pdfUrl ? (
              <div className="w-full h-full flex flex-col">
                <div className="flex-1 overflow-hidden">
                  {/* Use iframe instead of object to better handle CORS issues */}
                  <div className="relative w-full h-full">
                    <iframe
                      src={`${pdfUrl}#view=FitH`}
                      className="w-full h-full"
                      onLoad={() => setLoading(false)}
                      onError={() => {
                        setLoading(false);
                        setPdfError(true);
                      }}
                      title="PDF Viewer"
                    />
                  </div>
                </div>

                {/* Error Message */}
                {pdfError && (
                  <div className="absolute inset-0 bg-white bg-opacity-90 flex flex-col items-center justify-center">
                    <div className="text-red-500 text-xl mb-4">PDF konnte nicht geladen werden</div>
                    <a
                      href={pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:text-blue-700 underline"
                    >
                      PDF in neuem Tab öffnen
                    </a>
                    <button
                      onClick={() => setViewMode('data')}
                      className="mt-4 border border-blue-500 text-blue-500 hover:bg-blue-50 px-4 py-2 rounded-md"
                    >
                      Schließen
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                <p className="mb-4">Keine PDF-Datei verfügbar</p>
                <button
                  onClick={() => setViewMode('data')}
                  className="text-blue-500 hover:text-blue-600 border border-blue-500 px-4 py-2 rounded-md"
                >
                  Schließen
                </button>
              </div>
            )}
          </div>
        ) : viewMode === 'split' ? (
          <div className="flex-1 flex flex-row overflow-hidden">
            {/* Left side - Data - Complete version */}
            <div className="w-1/2 border-r overflow-y-auto relative">
              {/* Edit mode indicator for split view */}
              {isEditMode && (
                <div className="absolute top-2 right-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-md text-xs font-medium z-10">
                  Bearbeitungsmodus
                </div>
              )}
              <div className="p-3">
                {/* Document Info */}
                <section className="mb-4">
                  <h3 className="text-sm font-semibold mb-2">Dokument Information</h3>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-xs text-gray-600">Lieferscheinnummer</p>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={documentInfo?.documentNumber || ''}
                          onChange={(e) => handleFieldChange('standard.documentInfo.documentNumber', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{documentInfo?.documentNumber || '-'}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Datum</p>
                      {isEditMode ? (
                        <input
                          type="date"
                          value={documentInfo?.date || ''}
                          onChange={(e) => handleFieldChange('standard.documentInfo.date', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{documentInfo?.date || '-'}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Zeitstempel</p>
                      {isEditMode ? (
                        <input
                          type="datetime-local"
                          value={documentInfo?.timeStamp || ''}
                          onChange={(e) => handleFieldChange('standard.documentInfo.timeStamp', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{formatDateTime(documentInfo?.timeStamp)}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Dokumententyp</p>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={documentInfo?.type || ''}
                          onChange={(e) => handleFieldChange('standard.documentInfo.type', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{documentInfo?.type || '-'}</p>
                      )}
                    </div>
                  </div>
                </section>

                {/* Supplier Info */}
                <section className="mb-4">
                  <h3 className="text-sm font-semibold mb-2">Lieferant</h3>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="col-span-2">
                      <p className="text-xs text-gray-600">Name</p>
                      {isEditMode ? (
                        <textarea
                          value={documentInfo?.supplier?.name || ''}
                          onChange={(e) => handleFieldChange('standard.documentInfo.supplier.name', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                          rows={2}
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900 whitespace-pre-wrap">{documentInfo?.supplier?.name || '-'}</p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-gray-600">Adresse</p>
                      {isEditMode ? (
                        <textarea
                          value={documentInfo?.supplier?.address || ''}
                          onChange={(e) => handleFieldChange('standard.documentInfo.supplier.address', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                          rows={2}
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900 whitespace-pre-wrap">{documentInfo?.supplier?.address || '-'}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Telefon</p>
                      {isEditMode ? (
                        <input
                          type="tel"
                          value={documentInfo?.supplier?.contactInfo?.phone || ''}
                          onChange={(e) => handleFieldChange('standard.documentInfo.supplier.contactInfo.phone', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{documentInfo?.supplier?.contactInfo?.phone || '-'}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">E-Mail</p>
                      {isEditMode ? (
                        <input
                          type="email"
                          value={documentInfo?.supplier?.contactInfo?.email || ''}
                          onChange={(e) => handleFieldChange('standard.documentInfo.supplier.contactInfo.email', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{documentInfo?.supplier?.contactInfo?.email || '-'}</p>
                      )}
                    </div>
                  </div>
                </section>

                {/* Customer Info */}
                <section className="mb-4">
                  <h3 className="text-sm font-semibold mb-2">Kunde</h3>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="col-span-2">
                      <p className="text-xs text-gray-600">Name</p>
                      {isEditMode ? (
                        <textarea
                          value={documentInfo?.customer?.name || ''}
                          onChange={(e) => handleFieldChange('standard.documentInfo.customer.name', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                          rows={2}
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900 whitespace-pre-wrap">{documentInfo?.customer?.name || '-'}</p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-gray-600">Adresse</p>
                      {isEditMode ? (
                        <textarea
                          value={documentInfo?.customer?.address || ''}
                          onChange={(e) => handleFieldChange('standard.documentInfo.customer.address', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                          rows={2}
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900 whitespace-pre-wrap">{documentInfo?.customer?.address || '-'}</p>
                      )}
                    </div>
                  </div>
                </section>

                {/* Production Plant */}
                <section className="mb-4">
                  <h3 className="text-sm font-semibold mb-2">Mischanlage</h3>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="col-span-2">
                      <p className="text-xs text-gray-600">Name</p>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={productionPlant?.name || ''}
                          onChange={(e) => handleFieldChange('standard.productionPlant.name', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{productionPlant?.name || '-'}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Adresse</p>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={productionPlant?.location?.address || ''}
                          onChange={(e) => handleFieldChange('standard.productionPlant.location.address', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{productionPlant?.location?.address || '-'}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Stadt</p>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={productionPlant?.location?.city || ''}
                          onChange={(e) => handleFieldChange('standard.productionPlant.location.city', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{productionPlant?.location?.city || '-'}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">PLZ</p>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={productionPlant?.location?.postalCode || ''}
                          onChange={(e) => handleFieldChange('standard.productionPlant.location.postalCode', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{productionPlant?.location?.postalCode || '-'}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Telefon</p>
                      {isEditMode ? (
                        <input
                          type="tel"
                          value={productionPlant?.contactInfo?.phone || ''}
                          onChange={(e) => handleFieldChange('standard.productionPlant.contactInfo.phone', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{productionPlant?.contactInfo?.phone || '-'}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">E-Mail</p>
                      {isEditMode ? (
                        <input
                          type="email"
                          value={productionPlant?.contactInfo?.email || ''}
                          onChange={(e) => handleFieldChange('standard.productionPlant.contactInfo.email', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{productionPlant?.contactInfo?.email || '-'}</p>
                      )}
                    </div>
                  </div>
                </section>

                {/* Construction Site */}
                <section className="mb-4">
                  <h3 className="text-sm font-semibold mb-2">Baustelle</h3>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-xs text-gray-600">Projektnummer</p>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={constructionSite?.projectNumber || ''}
                          onChange={(e) => handleFieldChange('standard.constructionSite.projectNumber', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{constructionSite?.projectNumber || '-'}</p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-gray-600">Standort</p>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={constructionSite?.location || ''}
                          onChange={(e) => handleFieldChange('standard.constructionSite.location', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{constructionSite?.location || '-'}</p>
                      )}
                    </div>
                  </div>
                </section>

                {/* Material Details */}
                <section className="mb-4">
                  <h3 className="text-sm font-semibold mb-2">Material</h3>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-xs text-gray-600">Sortennummer</p>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={material?.sortNumber || ''}
                          onChange={(e) => handleFieldChange('standard.material.sortNumber', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{material?.sortNumber || '-'}</p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-gray-600">Asphaltmischgut</p>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={material?.asphaltMixture || ''}
                          onChange={(e) => handleFieldChange('standard.material.asphaltMixture', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{material?.asphaltMixture || '-'}</p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-gray-600">Zusätzliche Information</p>
                      {isEditMode ? (
                        <textarea
                          value={material?.additionalInformation || ''}
                          onChange={(e) => handleFieldChange('standard.material.additionalInformation', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                          rows={2}
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900 whitespace-pre-wrap line-clamp-2">{material?.additionalInformation || '-'}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Brutto Gewicht</p>
                      {isEditMode ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            value={material?.quantities?.brutto?.value || ''}
                            onChange={(e) => handleFieldChange('standard.material.quantities.brutto.value', parseFloat(e.target.value))}
                            className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                          />
                          <input
                            type="text"
                            value={material?.quantities?.brutto?.unit || ''}
                            onChange={(e) => handleFieldChange('standard.material.quantities.brutto.unit', e.target.value)}
                            className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-20"
                            placeholder="Einheit"
                          />
                        </div>
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{formatWeight(material?.quantities?.brutto)}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Tara Gewicht</p>
                      {isEditMode ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            value={material?.quantities?.tara?.value || ''}
                            onChange={(e) => handleFieldChange('standard.material.quantities.tara.value', parseFloat(e.target.value))}
                            className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                          />
                          <input
                            type="text"
                            value={material?.quantities?.tara?.unit || ''}
                            onChange={(e) => handleFieldChange('standard.material.quantities.tara.unit', e.target.value)}
                            className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-20"
                            placeholder="Einheit"
                          />
                        </div>
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{formatWeight(material?.quantities?.tara)}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Netto Gewicht</p>
                      {isEditMode ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            value={material?.quantities?.netto?.value || ''}
                            onChange={(e) => handleFieldChange('standard.material.quantities.netto.value', parseFloat(e.target.value))}
                            className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                          />
                          <input
                            type="text"
                            value={material?.quantities?.netto?.unit || ''}
                            onChange={(e) => handleFieldChange('standard.material.quantities.netto.unit', e.target.value)}
                            className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-20"
                            placeholder="Einheit"
                          />
                        </div>
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{formatWeight(material?.quantities?.netto)}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">CO2 Wert (kg CO₂e/t)</p>
                      {isEditMode ? (
                        <input
                          type="number"
                          value={material?.co2Value || ''}
                          onChange={(e) => handleFieldChange('standard.material.co2Value', parseFloat(e.target.value))}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{material?.co2Value || '-'}</p>
                      )}
                    </div>
                  </div>
                </section>

                {/* Transport */}
                <section className="mb-4">
                  <h3 className="text-sm font-semibold mb-2">Transport</h3>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-xs text-gray-600">Fahrzeug Kennzeichen</p>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={data?.standard?.transport?.vehicle?.FahrzeugKennzeichen || ''}
                          onChange={(e) => handleFieldChange('standard.transport.vehicle.FahrzeugKennzeichen', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{data?.standard?.transport?.vehicle?.FahrzeugKennzeichen || '-'}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Fahrzeugtyp</p>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={data?.standard?.transport?.vehicle?.type || ''}
                          onChange={(e) => handleFieldChange('standard.transport.vehicle.type', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{data?.standard?.transport?.vehicle?.type || '-'}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Wiegung Brutto</p>
                      {isEditMode ? (
                        <input
                          type="datetime-local"
                          value={data?.standard?.transport?.weighing?.gross || ''}
                          onChange={(e) => handleFieldChange('standard.transport.weighing.gross', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{formatDateTime(data?.standard?.transport?.weighing?.gross)}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Wiegung Tara</p>
                      {isEditMode ? (
                        <input
                          type="datetime-local"
                          value={data?.standard?.transport?.weighing?.tare || ''}
                          onChange={(e) => handleFieldChange('standard.transport.weighing.tare', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{formatDateTime(data?.standard?.transport?.weighing?.tare)}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Spediteur</p>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={data?.standard?.transport?.carrier?.name || ''}
                          onChange={(e) => handleFieldChange('standard.transport.carrier.name', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{data?.standard?.transport?.carrier?.name || '-'}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Spediteur ID</p>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={data?.standard?.transport?.carrier?.transporterId || ''}
                          onChange={(e) => handleFieldChange('standard.transport.carrier.transporterId', e.target.value)}
                          className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-1 w-full"
                        />
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900">{data?.standard?.transport?.carrier?.transporterId || '-'}</p>
                      )}
                    </div>
                  </div>
                </section>
              </div>
            </div>

            {/* Right side - PDF */}
            <div className="w-1/2 bg-gray-100 relative">

              {/* Reprocessing status */}
              {isReprocessing && (
                <div className="absolute top-2 right-2 left-2 z-20 bg-white p-2 rounded-md shadow-md text-xs">
                  <div className="flex flex-col gap-1">
                    <p className="font-medium">Status: {(() => {
                      // Filter out verbose technical messages from reprocessing status
                      if (!reprocessingStatus) return 'Processing...';
                      if (reprocessingStatus.includes('=== STEP') ||
                          reprocessingStatus.includes('Vision API Processing') ||
                          reprocessingStatus.includes('Application will restart to free memory') ||
                          reprocessingStatus.includes('Starting processing') ||
                          reprocessingStatus.includes('Start Processing')) {
                        return 'Processing...';
                      }
                      return reprocessingStatus;
                    })()}</p>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${reprocessingProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-gray-600">{reprocessingProgress}% abgeschlossen</p>
                  </div>
                </div>
              )}

              {loading && (
                <div className="absolute inset-0 flex items-center justify-center z-10">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
              )}

              {pdfUrl ? (
                <div className="w-full h-full">
                  {/* Use iframe instead of object to better handle CORS issues */}
                  <div className="relative w-full h-full">
                    <iframe
                      src={`${pdfUrl}#view=FitH`}
                      className="w-full h-full"
                      onLoad={() => setLoading(false)}
                      onError={() => {
                        setLoading(false);
                        setPdfError(true);
                      }}
                      title="PDF Viewer (Split View)"
                    />
                  </div>
                </div>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-gray-500">
                  <p>Keine PDF-Datei verfügbar</p>
                </div>
              )}

              {/* PDF Error in Split View */}
              {pdfError && (
                <div className="absolute inset-0 bg-white bg-opacity-90 flex flex-col items-center justify-center">
                  <div className="text-red-500 mb-2">PDF konnte nicht geladen werden</div>
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-500 hover:text-blue-700 underline"
                  >
                    In neuem Tab öffnen
                  </a>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="p-6 overflow-y-auto relative">
            {/* Edit mode indicator */}
            {isEditMode && (
              <div className="absolute top-2 right-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-md text-xs font-medium">
                Bearbeitungsmodus
              </div>
            )}
            {/* Document Info */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Dokument Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Lieferscheinnummer</p>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={documentInfo?.documentNumber || ''}
                      onChange={(e) => handleFieldChange('standard.documentInfo.documentNumber', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{documentInfo?.documentNumber || '-'}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Datum</p>
                  {isEditMode ? (
                    <input
                      type="date"
                      value={documentInfo?.date || ''}
                      onChange={(e) => handleFieldChange('standard.documentInfo.date', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{documentInfo?.date || '-'}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Zeitstempel</p>
                  {isEditMode ? (
                    <input
                      type="datetime-local"
                      value={documentInfo?.timeStamp || ''}
                      onChange={(e) => handleFieldChange('standard.documentInfo.timeStamp', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{formatDateTime(documentInfo?.timeStamp)}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Dokumententyp</p>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={documentInfo?.type || ''}
                      onChange={(e) => handleFieldChange('standard.documentInfo.type', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{documentInfo?.type || '-'}</p>
                  )}
                </div>
              </div>
            </section>

            {/* Supplier Info */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Lieferant</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Name</p>
                  {isEditMode ? (
                    <textarea
                      value={documentInfo?.supplier?.name || ''}
                      onChange={(e) => handleFieldChange('standard.documentInfo.supplier.name', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                      rows={3}
                    />
                  ) : (
                    <p className="mt-1 whitespace-pre-wrap">{documentInfo?.supplier?.name || '-'}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Adresse</p>
                  {isEditMode ? (
                    <textarea
                      value={documentInfo?.supplier?.address || ''}
                      onChange={(e) => handleFieldChange('standard.documentInfo.supplier.address', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                      rows={3}
                    />
                  ) : (
                    <p className="mt-1 whitespace-pre-wrap">{documentInfo?.supplier?.address || '-'}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Telefon</p>
                  {isEditMode ? (
                    <input
                      type="tel"
                      value={documentInfo?.supplier?.contactInfo?.phone || ''}
                      onChange={(e) => handleFieldChange('standard.documentInfo.supplier.contactInfo.phone', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{documentInfo?.supplier?.contactInfo?.phone || '-'}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">E-Mail</p>
                  {isEditMode ? (
                    <input
                      type="email"
                      value={documentInfo?.supplier?.contactInfo?.email || ''}
                      onChange={(e) => handleFieldChange('standard.documentInfo.supplier.contactInfo.email', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{documentInfo?.supplier?.contactInfo?.email || '-'}</p>
                  )}
                </div>
              </div>
            </section>

            {/* Customer Info */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Kunde</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Name</p>
                  {isEditMode ? (
                    <textarea
                      value={documentInfo?.customer?.name || ''}
                      onChange={(e) => handleFieldChange('standard.documentInfo.customer.name', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                      rows={3}
                    />
                  ) : (
                    <p className="mt-1 whitespace-pre-wrap">{documentInfo?.customer?.name || '-'}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Adresse</p>
                  {isEditMode ? (
                    <textarea
                      value={documentInfo?.customer?.address || ''}
                      onChange={(e) => handleFieldChange('standard.documentInfo.customer.address', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                      rows={3}
                    />
                  ) : (
                    <p className="mt-1 whitespace-pre-wrap">{documentInfo?.customer?.address || '-'}</p>
                  )}
                </div>
              </div>
            </section>

            {/* Production Plant */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Mischanlage</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Name</p>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={productionPlant?.name || ''}
                      onChange={(e) => handleFieldChange('standard.productionPlant.name', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{productionPlant?.name || '-'}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Adresse</p>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={productionPlant?.location?.address || ''}
                      onChange={(e) => handleFieldChange('standard.productionPlant.location.address', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{productionPlant?.location?.address || '-'}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Stadt</p>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={productionPlant?.location?.city || ''}
                      onChange={(e) => handleFieldChange('standard.productionPlant.location.city', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{productionPlant?.location?.city || '-'}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">PLZ</p>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={productionPlant?.location?.postalCode || ''}
                      onChange={(e) => handleFieldChange('standard.productionPlant.location.postalCode', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{productionPlant?.location?.postalCode || '-'}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Telefon</p>
                  {isEditMode ? (
                    <input
                      type="tel"
                      value={productionPlant?.contactInfo?.phone || ''}
                      onChange={(e) => handleFieldChange('standard.productionPlant.contactInfo.phone', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{productionPlant?.contactInfo?.phone || '-'}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">E-Mail</p>
                  {isEditMode ? (
                    <input
                      type="email"
                      value={productionPlant?.contactInfo?.email || ''}
                      onChange={(e) => handleFieldChange('standard.productionPlant.contactInfo.email', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{productionPlant?.contactInfo?.email || '-'}</p>
                  )}
                </div>
              </div>
            </section>

            {/* Construction Site */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Baustelle</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Projektnummer</p>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={constructionSite?.projectNumber || ''}
                      onChange={(e) => handleFieldChange('standard.constructionSite.projectNumber', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{constructionSite?.projectNumber || '-'}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Standort</p>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={constructionSite?.location || ''}
                      onChange={(e) => handleFieldChange('standard.constructionSite.location', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{constructionSite?.location || '-'}</p>
                  )}
                </div>
              </div>
            </section>

            {/* Material Details */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Material</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Sortennummer</p>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={material?.sortNumber || ''}
                      onChange={(e) => handleFieldChange('standard.material.sortNumber', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{material?.sortNumber || '-'}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Asphaltmischgut</p>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={material?.asphaltMixture || ''}
                      onChange={(e) => handleFieldChange('standard.material.asphaltMixture', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{material?.asphaltMixture || '-'}</p>
                  )}
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-600">Zusätzliche Information</p>
                  {isEditMode ? (
                    <textarea
                      value={material?.additionalInformation || ''}
                      onChange={(e) => handleFieldChange('standard.material.additionalInformation', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                      rows={3}
                    />
                  ) : (
                    <p className="mt-1 whitespace-pre-wrap">{material?.additionalInformation || '-'}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Brutto Gewicht</p>
                  {isEditMode ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={material?.quantities?.brutto?.value || ''}
                        onChange={(e) => handleFieldChange('standard.material.quantities.brutto.value', parseFloat(e.target.value))}
                        className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                      />
                      <input
                        type="text"
                        value={material?.quantities?.brutto?.unit || ''}
                        onChange={(e) => handleFieldChange('standard.material.quantities.brutto.unit', e.target.value)}
                        className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-24"
                        placeholder="Einheit"
                      />
                    </div>
                  ) : (
                    <p className="mt-1">{formatWeight(material?.quantities?.brutto)}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Tara Gewicht</p>
                  {isEditMode ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={material?.quantities?.tara?.value || ''}
                        onChange={(e) => handleFieldChange('standard.material.quantities.tara.value', parseFloat(e.target.value))}
                        className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                      />
                      <input
                        type="text"
                        value={material?.quantities?.tara?.unit || ''}
                        onChange={(e) => handleFieldChange('standard.material.quantities.tara.unit', e.target.value)}
                        className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-24"
                        placeholder="Einheit"
                      />
                    </div>
                  ) : (
                    <p className="mt-1">{formatWeight(material?.quantities?.tara)}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Netto Gewicht</p>
                  {isEditMode ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={material?.quantities?.netto?.value || ''}
                        onChange={(e) => handleFieldChange('standard.material.quantities.netto.value', parseFloat(e.target.value))}
                        className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                      />
                      <input
                        type="text"
                        value={material?.quantities?.netto?.unit || ''}
                        onChange={(e) => handleFieldChange('standard.material.quantities.netto.unit', e.target.value)}
                        className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-24"
                        placeholder="Einheit"
                      />
                    </div>
                  ) : (
                    <p className="mt-1">{formatWeight(material?.quantities?.netto)}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">CO2 Wert (kg CO₂e/t)</p>
                  {isEditMode ? (
                    <input
                      type="number"
                      value={material?.co2Value || ''}
                      onChange={(e) => handleFieldChange('standard.material.co2Value', parseFloat(e.target.value))}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{material?.co2Value || '-'}</p>
                  )}
                </div>
              </div>
            </section>

            {/* Transport */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Transport</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Fahrzeug Kennzeichen</p>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={data?.standard?.transport?.vehicle?.FahrzeugKennzeichen || ''}
                      onChange={(e) => handleFieldChange('standard.transport.vehicle.FahrzeugKennzeichen', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{data?.standard?.transport?.vehicle?.FahrzeugKennzeichen || '-'}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Fahrzeugtyp</p>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={data?.standard?.transport?.vehicle?.type || ''}
                      onChange={(e) => handleFieldChange('standard.transport.vehicle.type', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{data?.standard?.transport?.vehicle?.type || '-'}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Wiegung Brutto</p>
                  {isEditMode ? (
                    <input
                      type="datetime-local"
                      value={data?.standard?.transport?.weighing?.gross || ''}
                      onChange={(e) => handleFieldChange('standard.transport.weighing.gross', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{formatDateTime(data?.standard?.transport?.weighing?.gross)}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Wiegung Tara</p>
                  {isEditMode ? (
                    <input
                      type="datetime-local"
                      value={data?.standard?.transport?.weighing?.tare || ''}
                      onChange={(e) => handleFieldChange('standard.transport.weighing.tare', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{formatDateTime(data?.standard?.transport?.weighing?.tare)}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Spediteur</p>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={data?.standard?.transport?.carrier?.name || ''}
                      onChange={(e) => handleFieldChange('standard.transport.carrier.name', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{data?.standard?.transport?.carrier?.name || '-'}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Spediteur ID</p>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={data?.standard?.transport?.carrier?.transporterId || ''}
                      onChange={(e) => handleFieldChange('standard.transport.carrier.transporterId', e.target.value)}
                      className="mt-1 text-sm font-medium text-gray-900 border border-gray-300 rounded-md p-2 w-full"
                    />
                  ) : (
                    <p className="mt-1">{data?.standard?.transport?.carrier?.transporterId || '-'}</p>
                  )}
                </div>
              </div>
            </section>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Lieferschein löschen</h2>
            <p className="mb-6">
              Sind Sie sicher, dass Sie diesen Lieferschein <strong>{documentInfo?.documentNumber}</strong> löschen möchten?
              Diese Aktion kann nicht rückgängig gemacht werden.
            </p>

            {isDeleting ? (
              <div className="flex flex-col items-center justify-center py-4 mb-6">
                <Loader2 className="h-8 w-8 animate-spin text-red-600 mb-2" />
                <p className="text-gray-700">Lieferschein wird gelöscht...</p>
              </div>
            ) : (
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowDeleteConfirmation(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  disabled={isDeleting}
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  disabled={isDeleting}
                >
                  Löschen
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailedDeliveryNoteModal;